import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import Button from "../components/Button";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

const PRODUCTS = [
  {
    title: "Sports Impact Observatory",
    body:
      "Report trimestrale che fotografa lo stato del mercato sport-marketing italiano. Numeri, grafici, analisi qualitative, conclusioni operative. È il prodotto-faro del nostro lavoro di intelligence: disponibile in versione estratto gratuita (lead magnet), in versione integrale come prodotto standalone acquistabile da chiunque, o all'interno dei programmi di Impact Strategy & Advisory continuativi.",
  },
  {
    title: "Sport Impact Report — Corporate Edition",
    body:
      "Ricerca su misura per singolo cliente. Costruita sui suoi investimenti, sul suo posizionamento, sui suoi obiettivi. Esempio: come quantificare l'impatto sociale e di brand di una sponsorship pluriennale per il bilancio ESG di una banca.",
  },
  {
    title: "Fan Engagement & Data Analysis",
    body:
      "Analisi comportamentale della fanbase di una property (club, federazione, evento). Segmentazione, mappa delle motivazioni, previsioni di acquisto, benchmark vs settore. Per ottimizzare la strategia di acquisizione, fidelizzazione, upsell.",
  },
];

const METHOD_STEPS = [
  {
    num: "01",
    title: "Definizione della domanda",
    body:
      "Cosa il cliente vuole davvero sapere. Distinguiamo la domanda formale (\"voglio capire il mio fan engagement\") dalla domanda reale (\"voglio decidere se investire nel programma fedeltà nel 2026\"). La ricerca lavora sulla seconda.",
  },
  {
    num: "02",
    title: "Design metodologico",
    body:
      "Identificazione delle fonti dati, definizione del campione, scelta degli strumenti analitici. Documentazione esplicita di limiti e ipotesi. Niente \"ricerca magica\" — il metodo è sempre trasparente.",
  },
  {
    num: "03",
    title: "Raccolta & analisi",
    body:
      "Esecuzione della ricerca. Survey, interviste, analisi quantitative su dati esistenti, social listening qualitativo, intelligence di mercato. Cross-verification dei risultati.",
  },
  {
    num: "04",
    title: "Insight & delivery",
    body:
      "Sintesi orientata alla decisione. Non un report da 200 pagine — un documento di 20-40 pagine con conclusioni operative, scenari di azione, raccomandazioni esplicite.",
  },
];

const DELIVERABLES = [
  { output: "Sports Impact Observatory", format: "PDF + dashboard interattiva", when: "Trimestrale" },
  { output: "Sport Impact Report Corporate", format: "PDF executive + dataset", when: "8-12 settimane" },
  { output: "Fan Engagement Analysis", format: "PDF + dashboard", when: "6-10 settimane" },
  { output: "Presentation deck al board", format: "PPT", when: "Su richiesta" },
  { output: "Workshop di restituzione", format: "Sessione di lavoro", when: "Su richiesta" },
];

const PROFILES = [
  {
    title: "Brand corporate per il bilancio ESG",
    body:
      "Aziende con obblighi crescenti di reporting di sostenibilità che hanno bisogno di quantificare l'impatto sociale e di brand dei propri investimenti sportivi.",
  },
  {
    title: "C-level che devono giustificare lo sport",
    body:
      "CMO, CFO, board che hanno bisogno di intelligence quantitativa per portare in approvazione un nuovo investimento sportivo o per difendere uno esistente.",
  },
  {
    title: "Property sportive in fase di vendita commerciale",
    body:
      "Club, leghe, federazioni che vogliono presentare la propria proposta commerciale a nuovi sponsor con dati propri di engagement, demografia, comportamento della fanbase.",
  },
];

const QUALITY = [
  {
    dim: "Trasparenza metodologica",
    note: "Metodologia documentata esplicitamente in ogni report. Limiti dichiarati. Fonti citate.",
  },
  {
    dim: "Campioni statisticamente solidi",
    note: "Dimensione del campione adeguata alle conclusioni che trae. Documentazione esplicita degli intervalli di confidenza.",
  },
  {
    dim: "Indipendenza",
    note:
      "La ricerca non viene piegata a una conclusione preconfezionata. Quando i dati non confermano l'ipotesi del cliente, lo diciamo.",
  },
  {
    dim: "Orientamento alla decisione",
    note: "Ogni report finisce con raccomandazioni operative, non con dati senza conclusioni.",
  },
];

const FAQS = [
  {
    q: "Le vostre ricerche sono indipendenti?",
    a:
      "Sì. Operiamo con metodologie esplicite e campioni trasparenti. Non vendiamo ricerca a brand per giustificare decisioni già prese — i clienti lo sanno e lo apprezzano.",
  },
  {
    q: "Producete anche white paper?",
    a:
      "Sì. Le sintesi dell'Observatory vengono periodicamente pubblicate come white paper gratuiti. La ricerca custom resta riservata al cliente, salvo accordi specifici.",
  },
  {
    q: "Lavorate con dati esterni o solo con i nostri?",
    a:
      "Entrambi. Per la ricerca custom integriamo dati proprietari del cliente con benchmark di mercato, fonti pubbliche, analisi nostre. La trasparenza sulla provenienza dei dati è una delle condizioni con cui consegniamo il lavoro.",
  },
  {
    q: "Quali sono i tempi di consegna di uno Sport Impact Report Corporate?",
    a:
      "Dipende dalla complessità della domanda di ricerca. Un report standard richiede 8-12 settimane dalla sessione di scoping al delivery finale. Per ricerche più articolate, definiamo insieme una roadmap di consegna intermedia in fase di avvio.",
  },
  {
    q: "Come ricevo l'Observatory?",
    a:
      "L'estratto gratuito è disponibile alla pubblicazione tramite la nostra newsletter. L'edizione integrale può essere acquistata come prodotto standalone con sottoscrizione trimestrale, oppure è inclusa nei programmi Impact Strategy & Advisory continuativi.",
  },
];

const ServizioResearchInsight = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "Research & Insight", url: "/servizi/research-insight" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "Research & Insight",
    description: "Ricerca custom per brand sportivi: market sizing, benchmark, scenario planning.",
    slug: "research-insight",
  });

  return (
    <>
      <Seo
        title="Research & Insight: ricerca sponsorship sportiva | OnSports"
        description="Ricerca custom per brand sportivi: market sizing, benchmark, scenario planning, audience analysis. Output: report executive e raccomandazioni operative."
        path="/servizi/research-insight"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-research-insight"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="05" className="mt-8">
              Research &amp; Insight
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Ricerca proprietaria · Intelligence · Dati per il board
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">Decisioni</span>
              <span className="block">basate su dati,</span>
              <span className="block">
                non su <span className="text-lime">INTUIZIONI.</span>
              </span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Produciamo ricerca proprietaria, intelligence strategica e
              analisi quantitative per chi prende decisioni sportive in
              azienda. Report annuali, observatory trimestrali, analisi fan
              engagement. Per board, marketing strategico, comunicazione
              istituzionale, reporting ESG.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — C-level, CMO, CFO, CSR director, comitati ESG</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Report, observatory, dashboard, analisi quantitative</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  L'industria italiana dello sport non ha un sistema di
                  intelligence comune.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Quando un brand italiano deve decidere se investire in
                  una sponsorship, in che modo ripartire il budget tra
                  discipline, su quali property concentrarsi, su quali
                  nuovi territori puntare — la decisione si prende quasi
                  sempre senza dati. Si prende su opinioni, su relazioni
                  personali, su benchmark approssimativi presi da casi
                  internazionali poco trasferibili. È una scommessa basata
                  sulla speranza che chi decide abbia abbastanza esperienza
                  per scegliere bene.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Le ragioni di questo vuoto sono strutturali. L'Italia non
                  ha un equivalente robusto del lavoro che fanno Nielsen
                  Sports, Two Circles o l'European Sponsorship Association
                  in altri mercati. Le ricerche di settore disponibili sono
                  spesso datate, parziali, prodotte da operatori in
                  conflitto di interesse, o costruite su campioni non
                  statisticamente significativi. Il risultato è che ogni
                  anno, decine di milioni di euro di investimenti sportivi
                  italiani vengono allocati senza un'intelligence comune
                  del settore.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  La nostra ambizione è contribuire a costruirla — nella
                  scala in cui un'organizzazione delle nostre dimensioni
                  può farlo. Produciamo ricerca proprietaria su scala
                  italiana, intelligence specifica per i clienti che ce la
                  richiedono, analisi quantitative su singole property e
                  singoli segmenti. Lo facciamo con metodologie pubbliche
                  e con campioni trasparenti.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FACCIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-cosa-facciamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa facciamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre prodotti di ricerca.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[68ch] opacity-90">
                  Il servizio Research &amp; Insight si declina in tre
                  prodotti distinti. Possono essere acquistati separatamente
                  o in combinazione, a seconda delle esigenze del cliente.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PRODUCTS.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <div
                  className="border border-lime p-6 md:p-8 bg-galactico/[0.02] h-full flex flex-col"
                  data-testid={`product-${i}`}
                >
                  <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL METODO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-metodo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Il metodo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Trasparenza metodologica.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {METHOD_STEPS.map((step, i) => (
              <RevealOnScroll key={step.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-dodgers/[0.02] h-full flex flex-col"
                  data-testid={`method-step-${step.num}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-lime text-3xl md:text-4xl leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {step.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 5 — COSA INCLUDIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-includiamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa includiamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quello che ricevi.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="deliverables-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Output</th>
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Formato</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Tempi</th>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map((d, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top">{d.output}</td>
                      <td className="py-4 pr-6 align-top opacity-80">{d.format}</td>
                      <td className="py-4 align-top opacity-80">{d.when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — PER CHI È */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre tipologie di committente.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — INSIGHT IN PUBBLICO · VARIAZIONE (no card case study) */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-insight-pubblico">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto text-center">
            <RevealOnScroll>
              <SectionEyebrow>Gli insight in pubblico</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <h2 className="display-h2 mt-6">
                Il lavoro che possiamo mostrare, è qui.
              </h2>
            </RevealOnScroll>
            <RevealOnScroll delay={0.18}>
              <p className="mt-8 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] opacity-90 max-w-[720px] mx-auto">
                La maggior parte della ricerca che produciamo è custom per
                i clienti, sotto NDA e protetta. Quello che invece
                pubblichiamo apertamente — il riassunto dell'Observatory,
                gli articoli editoriali, i glossari — vive sulla nostra
                sezione News, dove costruiamo nel tempo l'intelligence
                aperta del nostro settore. È il modo migliore per capire
                come ragioniamo prima di parlarci.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <div className="mt-10 flex justify-center">
                <Link to="/news" data-testid="cta-insight-news">
                  <Button variant="primary">Leggi gli insight aperti</Button>
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — MISURAZIONE & KPI (qualità della ricerca) */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-kpi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Misurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Come misuriamo la qualità di una ricerca.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="kpi-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal w-[32%]">Dimensione</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Cosa garantiamo</th>
                  </tr>
                </thead>
                <tbody>
                  {QUALITY.map((k, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top font-display text-base md:text-lg">{k.dim}</td>
                      <td className="py-4 align-top opacity-90">{k.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — FAQ */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-galactico/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Costruiamo insieme le tue <span className="text-lime">DECISIONI</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              Una call di scoping di 30 minuti per capire se la ricerca esiste già o se va costruita su misura.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta consulenza — Research & Insight"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioResearchInsight;
